/**
 * Карточка скалярных показателей раздела: «показатель — значение — единица».
 *
 * У показателя, который обычно приходит из «Мониторинга СНП», рядом стоит
 * переключатель источника: пока выбран мониторинг, поле только для чтения.
 */
import { Lock } from 'lucide-react';

import { Card, CardContent, CardHeader } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { cn } from '../../../../lib/cn';
import type { Dtype, PassportField } from '../../../../requests/input_data.request';
import { pick, type Lang } from '../formState';

export interface FieldsCardProps {
  /** Заголовок раздела. Пустая строка — не показывать: он уже есть над карточкой. */
  title: string;
  fields: PassportField[];
  lang: Lang;
  values: Record<string, string>;
  initial: Record<string, string>;
  onChange: (key: string, value: string) => void;
  /** Ключи, переведённые оператором на ручной ввод. */
  manualKeys: Set<string>;
  onToggleManual: (key: string, manual: boolean) => void;
  labels: { source: string; sourceSnp: string; sourceManual: string };
}

export const inputTypeOf = (dtype: Dtype): string => {
  if (dtype === 'date') return 'date';
  if (dtype === 'str') return 'text';
  return 'number';
};

export const FieldsCard = ({
  title,
  fields,
  lang,
  values,
  initial,
  onChange,
  manualKeys,
  onToggleManual,
  labels,
}: FieldsCardProps) => {
  if (fields.length === 0) return null;

  return (
    <Card className="shadow-card" data-testid="fields-card">
      {title !== '' && (
        <CardHeader className="pb-3">
          <h3 className="text-base font-semibold">{title}</h3>
        </CardHeader>
      )}
      {/* Без заголовка карточке нужен свой верхний отступ: `pt-0` рассчитан на
          шапку выше. */}
      <CardContent className={cn('pt-0', title === '' && 'pt-4')}>
        <ul className="divide-y divide-surface-border">
          {fields.map((field) => {
            const fromSnp = field.snp_source !== null;
            const manual = !fromSnp || manualKeys.has(field.key);
            const value = values[field.key] ?? '';
            const changed = value !== (initial[field.key] ?? '');
            return (
              <li
                key={field.key}
                className="flex flex-wrap items-center justify-between gap-3 py-2"
              >
                <span className="min-w-0 text-sm">
                  {pick(field.label_ru, field.label_kz, lang)}
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {fromSnp && (
                    <label className="flex items-center gap-1.5 text-xs text-text-muted">
                      {!manual && <Lock className="size-3.5" />}
                      <select
                        value={manual ? 'manual' : 'source'}
                        aria-label={labels.source}
                        data-testid={`source-${field.key}`}
                        onChange={(event) =>
                          onToggleManual(field.key, event.target.value === 'manual')
                        }
                        className="h-9 rounded-md border border-surface-border bg-white px-2 text-xs"
                      >
                        <option value="source">{labels.sourceSnp}</option>
                        <option value="manual">{labels.sourceManual}</option>
                      </select>
                    </label>
                  )}
                  {field.dtype === 'bool' ? (
                    <input
                      type="checkbox"
                      checked={value === 'true'}
                      disabled={!manual}
                      aria-label={pick(field.label_ru, field.label_kz, lang)}
                      data-testid={`input-${field.key}`}
                      onChange={(event) =>
                        onChange(field.key, event.target.checked ? 'true' : 'false')
                      }
                      className="size-5"
                    />
                  ) : (
                    <Input
                      type={inputTypeOf(field.dtype)}
                      inputMode={field.dtype === 'str' ? undefined : 'decimal'}
                      value={value}
                      disabled={!manual}
                      aria-label={pick(field.label_ru, field.label_kz, lang)}
                      data-testid={`input-${field.key}`}
                      onChange={(event) => onChange(field.key, event.target.value)}
                      className={cn(
                        'h-9 max-w-48 text-right tabular-nums',
                        changed && 'border-brand-500 bg-brand-50',
                        !manual && 'bg-surface-muted text-text-muted',
                      )}
                    />
                  )}
                  <span className="w-20 text-xs text-text-muted">
                    {field.unit ?? ''}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};
